import React, { useMemo, useState } from "react";

const OCCASIONS = [
  "Birthday",     "Anniversary", "Valentine's", "Thank You",
  "Christmas",    "Get Well Soon", "New Baby",  "Wedding",
];
const RECIPIENTS = [
  "Partner",      "Husband",    "Wife",        "Boyfriend", "Girlfriend",
  "Mum",          "Dad",        "Friend",      "Child",     "Grandparent",
];
const TONES = ["Funny", "Romantic", "Heartfelt", "Sweet", "Simple", "Sentimental"];

const C = {
  pageBg:     "#DDF0EF",
  dark:       "#1E3A5F",
  mid:        "#2D5A8E",
  accent:     "#4A7AAF",
  muted:      "#7A9BBF",
  chipBg:     "#EAF1F9",
  chipBorder: "#B8CDE0",
  cardBorder: "#D0DFF0",
};

const SK = {
  fill:           "none",
  stroke:         "#7EC8C1",
  strokeWidth:    "1.8",
  strokeLinecap:  "round",
  strokeLinejoin: "round",
};

function Gift({ x, y, sc = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${sc})`}>
      <rect x="-16" y="-5" width="32" height="20" rx="2" {...SK} />
      <rect x="-16" y="-12" width="32" height="7" rx="1" {...SK} />
      <line x1="0" y1="-12" x2="0" y2="15" {...SK} />
      <path d="M0,-12 C-5,-20 -11,-14 -5,-10 C-2,-8 0,-12 0,-12 C0,-12 2,-8 5,-10 C11,-14 5,-20 0,-12Z" {...SK} />
    </g>
  );
}

function Balloon({ x, y, sc = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${sc})`}>
      <ellipse cx="0" cy="-14" rx="13" ry="17" {...SK} />
      <path d="M0,3 C-3,8 3,11 0,17" {...SK} />
      <circle cx="0" cy="3" r="2" fill="#7EC8C1" stroke="none" />
    </g>
  );
}

function Cake({ x, y, sc = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${sc})`}>
      <rect x="-16" y="-7" width="32" height="20" rx="2" {...SK} />
      <rect x="-11" y="-16" width="22" height="9" rx="1" {...SK} />
      <line x1="-7" y1="-16" x2="-7" y2="-23" {...SK} />
      <line x1="0"  y1="-16" x2="0"  y2="-24" {...SK} />
      <line x1="7"  y1="-16" x2="7"  y2="-23" {...SK} />
      <ellipse cx="-7" cy="-24" rx="2" ry="1.5" {...SK} />
      <ellipse cx="0"  cy="-25" rx="2" ry="1.5" {...SK} />
      <ellipse cx="7"  cy="-24" rx="2" ry="1.5" {...SK} />
      <path d="M-16,0 C-11,-3 -5,1 0,-2 C5,-5 11,-1 16,-3" {...SK} />
    </g>
  );
}

function Heart({ x, y, sc = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${sc})`}>
      <path d="M0,12 C-14,-2 -20,-14 -10,-19 C-5,-22 0,-16 0,-16 C0,-16 5,-22 10,-19 C20,-14 14,-2 0,12Z" {...SK} />
    </g>
  );
}

function StarShape({ x, y, sc = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${sc})`}>
      <path d="M0,-16 L3.8,-5 L15,-5 L6.2,2 L9.5,13 L0,6.5 L-9.5,13 L-6.2,2 L-15,-5 L-3.8,-5Z" {...SK} />
    </g>
  );
}

function Envelope({ x, y, sc = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${sc})`}>
      <rect x="-16" y="-11" width="32" height="22" rx="2" {...SK} />
      <path d="M-16,-11 L0,4 L16,-11" {...SK} />
    </g>
  );
}

function BgIcons() {
  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Corners — large */}
      <Gift      x={55}   y={90}   sc={3.2} />
      <Balloon   x={1385} y={85}   sc={3.2} />
      <Cake      x={55}   y={820}  sc={2.8} />
      <Heart     x={1385} y={820}  sc={3.0} />

      {/* Mid-edge */}
      <StarShape x={38}   y={440}  sc={2.5} />
      <Envelope  x={1402} y={440}  sc={2.5} />

      {/* Top scatter */}
      <Balloon   x={280}  y={52}   sc={1.9} />
      <Gift      x={560}  y={44}   sc={1.7} />
      <StarShape x={880}  y={56}   sc={1.6} />
      <Heart     x={1160} y={46}   sc={1.8} />

      {/* Bottom scatter */}
      <Envelope  x={220}  y={868}  sc={1.8} />
      <Cake      x={500}  y={876}  sc={1.6} />
      <Balloon   x={780}  y={862}  sc={1.7} />
      <StarShape x={1060} y={872}  sc={1.6} />
      <Gift      x={1300} y={860}  sc={1.9} />

      {/* Mid-field accents */}
      <Heart     x={140}  y={240}  sc={1.3} />
      <Envelope  x={1300} y={220}  sc={1.3} />
      <Balloon   x={150}  y={650}  sc={1.2} />
      <StarShape x={1290} y={660}  sc={1.2} />
    </svg>
  );
}

export default function CardFinder() {
  const [occasion,  setOccasion]  = useState("Birthday");
  const [recipient, setRecipient] = useState("Partner");
  const [tone,      setTone]      = useState("Simple");
  const [extra,     setExtra]     = useState("");

  const searchQuery = useMemo(() => {
    const parts = [
      occasion,
      "card for",
      recipient.toLowerCase(),
      tone.toLowerCase(),
      extra.trim(),
    ].filter(Boolean);
    return parts.join(" ").replace(/\s+/g, " ").trim();
  }, [occasion, recipient, tone, extra]);

  const buildUrl = (site) => {
    const q = encodeURIComponent(searchQuery);
    return {
      moonpig: `https://www.moonpig.com/uk/search?q=${q}`,
      etsy:    `https://www.etsy.com/search?q=${q}`,
      amazon:  `https://www.amazon.co.uk/s?k=${q}&tag=findtherightc-21`,
    }[site];
  };

  return (
    <main
      style={{
        background:      C.pageBg,
        backgroundImage: "repeating-linear-gradient(45deg, rgba(26,138,122,0.07) 0px, rgba(26,138,122,0.07) 1px, transparent 1px, transparent 22px)",
        minHeight:       "100vh",
        position:        "relative",
        overflow:        "hidden",
      }}
      className="flex items-center justify-center p-4"
    >
      <BgIcons />

      <div style={{ maxWidth: 980, position: "relative", zIndex: 1 }} className="w-full">

        <header className="text-center mb-4">
          <h1 style={{ color: C.dark, fontSize: 34, fontWeight: 700, letterSpacing: "-0.5px" }}>
            Find the Right Card
          </h1>
          <p style={{ color: C.accent, fontSize: 15, marginTop: 6 }}>
            Pick your options and search Moonpig, Etsy &amp; Amazon in one click
          </p>
        </header>

        <div className="bg-white rounded-3xl" style={{ border: `1px solid ${C.cardBorder}`, padding: "24px 44px" }}>

          <Section label="Occasion">
            <ChipGrid options={OCCASIONS} cols={4} selected={occasion} onSelect={setOccasion}
              mobileHide={["Thank You", "Get Well Soon"]} />
          </Section>

          <Section label="Who's it for?">
            <ChipGrid options={RECIPIENTS} cols={5} selected={recipient} onSelect={setRecipient}
              mobileHide={["Husband", "Wife", "Boyfriend", "Girlfriend"]} />
          </Section>

          <Section label="Tone">
            <ChipGrid options={TONES} cols={3} selected={tone} onSelect={setTone} />
          </Section>

          <Section label="Anything more specific? (optional)" last>
            <input
              type="text"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="e.g. husband, boyfriend, thank you, non-binary, step-mum…"
              style={{
                width:        "100%",
                boxSizing:    "border-box",
                border:       `1.5px solid ${C.chipBorder}`,
                borderRadius: 10,
                padding:      "13px 16px",
                fontSize:     15,
                color:        C.dark,
                background:   "#F7FAFD",
                fontFamily:   "inherit",
                outline:      "none",
              }}
              onFocus={(e)  => (e.currentTarget.style.borderColor = C.accent)}
              onBlur={(e)   => (e.currentTarget.style.borderColor = C.chipBorder)}
            />
          </Section>

          <div className="text-center my-4" style={{ color: C.muted, fontSize: 15 }}>
            Searching for{" "}
            <span style={{ color: C.dark, fontWeight: 600 }}>&ldquo;{searchQuery}&rdquo;</span>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <ShopBtn label="Moonpig" href={buildUrl("moonpig")} />
            <ShopBtn label="Etsy"    href={buildUrl("etsy")}    />
            <ShopBtn label="Amazon"  href={buildUrl("amazon")}  />
          </div>
        </div>

        <footer className="text-center mt-3 text-xs" style={{ color: C.muted }}>
          {new Date().getFullYear()} FindTheRightCard.co.uk &middot; Links to Amazon, Moonpig &amp; Etsy may earn a small commission &middot;{" "}
          <a href="/privacy.html" style={{ color: C.muted, textDecoration: "underline" }}>Privacy Policy</a>
        </footer>
      </div>
    </main>
  );
}

function Section({ label, children, last = false }) {
  return (
    <div style={{ marginBottom: last ? 0 : 16 }}>
      <p style={{ color: C.accent, fontSize: 13, fontWeight: 500, marginBottom: 10, letterSpacing: "0.2px" }}>
        {label}
      </p>
      {children}
    </div>
  );
}

function ChipGrid({ options, cols, selected, onSelect, mobileHide = [] }) {
  return (
    <div className={`chip-grid chip-grid-${cols}`} style={{ gap: 8 }}>
      {options.map((opt) => {
        const active = selected === opt;
        const hidden = mobileHide.includes(opt) ? "chip-mobile-hide" : "";
        return (
          <button key={opt} type="button" onClick={() => onSelect(opt)}
            className={hidden}
            style={{
              background:   active ? C.dark  : C.chipBg,
              color:        active ? "#fff"  : C.dark,
              border:       `1px solid ${active ? C.dark : C.chipBorder}`,
              borderRadius: 8,
              padding:      "11px 8px",
              fontSize:     14,
              fontWeight:   active ? 600 : 400,
              cursor:       "pointer",
              transition:   "all 0.12s",
              textAlign:    "center",
              lineHeight:   1.3,
            }}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function ShopBtn({ label, href }) {
  return (
    <a href={href} target="_blank" rel="noreferrer"
      style={{
        display:        "block",
        background:     C.mid,
        color:          "#fff",
        borderRadius:   10,
        padding:        "16px 8px",
        textAlign:      "center",
        fontSize:       16,
        fontWeight:     600,
        textDecoration: "none",
        transition:     "background 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.dark)}
      onMouseLeave={(e) => (e.currentTarget.style.background = C.mid)}>
      {label}
    </a>
  );
}
